import React from 'react';
import { useAdminClinicList } from 'infrastructure/hooks/clinic/use-admin-clinic-list';
import { useTranslation } from 'react-i18next';
import { safeClamp } from 'utils/helpers/safeClamp';
import { ELASTIC_SEARCH_MAX_ITEMS } from 'constants/count';
import BasePagination from 'components/atoms/base-pagination';
import ClinicViewSelectBox from 'components/molecules/clinic-view-select-box';
import { ClinicsView } from 'infrastructure/enums';

import s from './styles.module.scss';
import DefaultViewTable from './defaultViewTable';
import ParentViewTable from './parentViewTable';

import type { IClinicListFilters } from 'infrastructure/interfaces';

interface IProps {
  filters: IClinicListFilters;
  updateFilter: (newFilter: Partial<IClinicListFilters>) => void;
}

const ClinicsTable: React.FC<IProps> = ({ filters, updateFilter }) => {
  const { t } = useTranslation();
  const { loading, clinics, meta } = useAdminClinicList({
    meta: {
      ...filters,
      onlyParents: filters.view === ClinicsView.ParentToChild,
    },
  });
  const onChangePage = (page: number) => {
    updateFilter({ page });
  };

  return (
    <div>
      <div className={s.header}>
        <p className={s.readings}>
          {t('labels.clinics')}: <span>{meta?.totalCount ?? 0}</span>
        </p>
        <div className={s['table-controller']}>
          <ClinicViewSelectBox
            value={filters.view}
            onChange={(view) => {
              updateFilter({
                view,
                page: undefined,
              });
            }}
            className={s['select-view']}
          />
          {meta.totalCount > 0 && (
            <BasePagination
              page={filters.page ?? 1}
              total={safeClamp(meta?.totalCount, 0, ELASTIC_SEARCH_MAX_ITEMS)}
              align="end"
              onChange={(newPage) => {
                updateFilter({ page: newPage });
              }}
              pageSize={100}
              pageSizeOptions={[]}
              hide={false}
              showSizeChanger={false}
            />
          )}
        </div>
      </div>
      {filters.view === ClinicsView.ParentToChild ? (
        <ParentViewTable
          clinics={clinics}
          onChangePage={onChangePage}
          filters={filters}
          loading={loading}
          meta={meta}
        />
      ) : (
        <DefaultViewTable
          clinics={clinics}
          onChangePage={onChangePage}
          filters={filters}
          loading={loading}
          meta={meta}
        />
      )}
    </div>
  );
};

export default ClinicsTable;
